import { memo, useState } from 'react';
import { Blend, Headphones, SlidersHorizontal, Wifi } from 'lucide-react';
import { Button, Modal, Tabs, type TabItem } from '@cyphex/ui';
import type { QualityProfile } from '@cyphex/types';
import type { ConnectionQuality, DeviceKind } from '../../media/contract';
import type {
  MediaPreferences, MediaProcessingStatus,
} from '../../media/settings/preferences';
import type { MediaPreferencesApi } from '../../hooks/useMediaPreferences';
import { AudioTab } from './AudioTab';
import { DevicesTab } from './DevicesTab';
import { EffectsTab } from './EffectsTab';
import { NetworkTab } from './NetworkTab';

type SettingsTab = 'devices' | 'audio' | 'effects' | 'network';

const TABS: readonly TabItem[] = [
  { value: 'devices', label: 'Devices', icon: SlidersHorizontal },
  { value: 'audio', label: 'Audio', icon: Headphones },
  { value: 'effects', label: 'Background', icon: Blend },
  { value: 'network', label: 'Network', icon: Wifi },
];

const isTab = (value: string): value is SettingsTab =>
  TABS.some((tab) => tab.value === value);

export interface MediaSettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preferences: MediaPreferences;
  api: MediaPreferencesApi;
  processing: MediaProcessingStatus;
  quality: ConnectionQuality;
  profile: QualityProfile;
  downlinkBps: number;
  packetLoss: number;
  degradedReason: string | null;
  /** Raw microphone capture, for the input-level meter. */
  micStream: MediaStream | null;
}

/** Devices, audio processing, background effects and network, in one place. */
export const MediaSettingsModal = memo(function MediaSettingsModal(props: MediaSettingsModalProps) {
  const [tab, setTab] = useState<SettingsTab>('devices');
  const { api, preferences } = props;

  const onSelectDevice = (kind: DeviceKind, deviceId: string): void =>
    api.setDevice(kind, deviceId);

  return (
    <Modal
      open={props.open}
      onOpenChange={props.onOpenChange}
      title="Settings"
      description="Everything here runs on your device. Nothing is processed on the server."
      size="xl"
      footer={
        <Button variant="primary" onClick={() => props.onOpenChange(false)}>
          Done
        </Button>
      }
    >
      <Tabs
        items={TABS}
        value={tab}
        variant="underline"
        onValueChange={(next) => {
          if (isTab(next)) setTab(next);
        }}
        className="mb-3"
      />

      {tab === 'devices' ? (
        <DevicesTab
          devices={preferences.devices}
          micStream={props.micStream}
          onSelect={onSelectDevice}
        />
      ) : null}

      {tab === 'audio' ? (
        <AudioTab
          audio={preferences.audio}
          runtime={props.processing.denoise}
          onChange={api.setAudio}
        />
      ) : null}

      {tab === 'effects' ? (
        <EffectsTab
          video={preferences.video}
          runtime={props.processing.effect}
          onChange={api.setVideo}
        />
      ) : null}

      {tab === 'network' ? (
        <NetworkTab
          quality={props.quality}
          profile={props.profile}
          downlinkBps={props.downlinkBps}
          packetLoss={props.packetLoss}
          lowBandwidth={preferences.lowBandwidth}
          degradedReason={props.degradedReason}
          onLowBandwidthChange={api.setLowBandwidth}
        />
      ) : null}
    </Modal>
  );
});
